import React, { useState, useEffect, useCallback } from "react";
import { XCircleIcon } from "@heroicons/react/24/outline";
import {
  UserGroupIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
} from "@heroicons/react/24/outline";

import {
  getSalesManagerUsers,
  getSalesManagerUserById,
  getUserActivitySummary,
} from "../../api/salesManager/usersAPI";
import { getUserAnnouncements } from "../../api/salesManager/announcementsAPI";
import { getActivityLogs } from "../../api/salesManager/activityLogsAPI";
import { getUserAnalytics } from "../../api/monitoringAPI";
import {
  getStoriesActiveHours,
  getStoriesPopularSearches,
  getStoriesCategoryInterest,
} from "../../api/salesManager/storiesAnalyticsAPI";

import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import UsersTab from "./tabs/UsersTab";
import AnalyticsTab from "./tabs/AnalyticsTab";
import ActivityTab from "./tabs/ActivityTab";
import StoriesAnalyticsTab from "./tabs/StoriesAnalyticsTab";
import { formatDate, getStatusBadge } from "./utils";

const navigationItems = [
  {
    id: "users",
    name: "Пайдаланушылар",
    icon: UserGroupIcon,
    color: "blue",
    description: "Пайдаланушылар тізімі",
  },
  {
    id: "analytics",
    name: "Аналитика",
    icon: PresentationChartLineIcon,
    color: "purple",
    description: "Статистикалар мен оқиғалар",
  },
  {
    id: "activity",
    name: "Белсенділік журналы",
    icon: ChartBarIcon,
    color: "green",
    description: "Белсенділік логи",
  },
  {
    id: "stories-analytics",
    name: "Сториз аналитикасы",
    icon: PresentationChartLineIcon,
    color: "purple",
    description: "Сториздер мен іздеулер аналитикасы",
  },
];

const SalesManagerDashboard = () => {
  const [activeTab, setActiveTab] = useState("users");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Users
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPage, setUserPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [userAnnouncements, setUserAnnouncements] = useState([]);
  const [userActivitySummary, setUserActivitySummary] = useState(null);
  const [userMonitoringAnalytics, setUserMonitoringAnalytics] = useState(null);

  // Activity logs
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalPages, setActivityTotalPages] = useState(1);
  const [activityFilters, setActivityFilters] = useState({
    user_id: "",
    action_type: "",
    entity_type: "",
    start_date: "",
    end_date: "",
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Stories analytics
  const [activeHoursData, setActiveHoursData] = useState(null);
  const [popularSearchesData, setPopularSearchesData] = useState(null);
  const [categoryInterestData, setCategoryInterestData] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(7);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const loadUsers = useCallback(async (page = 1, search = "") => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSalesManagerUsers({ page, limit: 10, search });
      setUsers(data.items || []);
      setUserTotalPages(data.pages || 1);
      setUserPage(page);
    } catch (err) {
      setError(`Қате: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUserDetails = async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const user = await getSalesManagerUserById(userId);
      setSelectedUser(user);

      const [announcementsData, activityData] = await Promise.all([
        getUserAnnouncements(userId, { page: 1, limit: 10 }),
        getUserActivitySummary(userId, 30),
      ]);
      setUserAnnouncements(announcementsData.items || []);
      setUserActivitySummary(activityData);

      try {
        const monitoringData = await getUserAnalytics(userId.toString());
        setUserMonitoringAnalytics(monitoringData);
      } catch {
        setUserMonitoringAnalytics(null);
      }
    } catch (err) {
      setError(`Қате: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityLogs = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);
      try {
        const params = { page, limit: 10 };
        if (activityFilters.user_id)
          params.user_id = parseInt(activityFilters.user_id);
        if (activityFilters.action_type)
          params.action_type = activityFilters.action_type;
        if (activityFilters.entity_type)
          params.entity_type = activityFilters.entity_type;
        if (activityFilters.start_date)
          params.start_date = activityFilters.start_date;
        if (activityFilters.end_date)
          params.end_date = activityFilters.end_date;

        const data = await getActivityLogs(params);
        setActivityLogs(data.items || []);
        setActivityTotalPages(data.pages || 1);
        setActivityPage(page);
      } catch (err) {
        setError(`Қате: ${err.message}`);
      } finally {
        setLoading(false);
      }
    },
    [activityFilters],
  );

  const loadStoriesAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    setError(null);
    try {
      const [activeHours, popularSearches, categoryInterest] =
        await Promise.all([
          getStoriesActiveHours({ days: analyticsDays }),
          getStoriesPopularSearches({ days: analyticsDays, limit: 20 }),
          getStoriesCategoryInterest({ days: analyticsDays }),
        ]);
      setActiveHoursData(activeHours);
      setPopularSearchesData(popularSearches);
      setCategoryInterestData(categoryInterest);
    } catch (err) {
      setError(`Қате: ${err.message}`);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [analyticsDays]);

  useEffect(() => {
    if (activeTab === "users") loadUsers(1, userSearch);
    else if (activeTab === "activity") loadActivityLogs(1);
    else if (activeTab === "stories-analytics") loadStoriesAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loadUsers, loadActivityLogs, loadStoriesAnalytics]);

  const handleLogout = () => {
    localStorage.clear();
    window.location.reload();
  };

  const handleUserSearch = (e) => {
    if (e.key === "Enter" || e.type === "click") {
      loadUsers(1, userSearch);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary flex">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-80 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        navigationItems={navigationItems}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        setSelectedUser={setSelectedUser}
        handleLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <TopBar
          activeTab={activeTab}
          navigationItems={navigationItems}
          onOpenSidebar={() => setSidebarOpen(true)}
          userSearch={userSearch}
          onUserSearchChange={setUserSearch}
          onUserSearchSubmit={handleUserSearch}
          onRefresh={() => {
            if (activeTab === "users") loadUsers(userPage, userSearch);
            else if (activeTab === "activity") loadActivityLogs(activityPage);
            else if (activeTab === "stories-analytics") loadStoriesAnalytics();
          }}
          refreshLoading={loading || analyticsLoading}
        />

        <div className="flex-1 p-6 page-bg overflow-auto min-h-0">
          {error && (
            <div className="mb-6 p-4 bg-red-900/30 border border-red-500/50 rounded-xl text-red-200 flex items-center space-x-3 animate-slide-in">
              <XCircleIcon className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {activeTab === "users" && (
            <UsersTab
              users={users}
              selectedUser={selectedUser}
              loading={loading}
              userPage={userPage}
              userTotalPages={userTotalPages}
              userSearch={userSearch}
              userAnnouncements={userAnnouncements}
              userActivitySummary={userActivitySummary}
              userMonitoringAnalytics={userMonitoringAnalytics}
              onSelectUser={loadUserDetails}
              onDeselect={() => setSelectedUser(null)}
              onPagePrev={() => loadUsers(Math.max(1, userPage - 1), userSearch)}
              onPageNext={() => loadUsers(userPage + 1, userSearch)}
              formatDate={formatDate}
              getStatusBadge={getStatusBadge}
            />
          )}

          {activeTab === "analytics" && <AnalyticsTab />}

          {activeTab === "activity" && (
            <ActivityTab
              activityLogs={activityLogs}
              activityPage={activityPage}
              activityTotalPages={activityTotalPages}
              loading={loading}
              activityFilters={activityFilters}
              filtersExpanded={filtersExpanded}
              onFiltersToggle={() => setFiltersExpanded((v) => !v)}
              onFilterChange={(key, value) =>
                setActivityFilters((prev) => ({ ...prev, [key]: value }))
              }
              onClearFilters={() =>
                setActivityFilters({
                  user_id: "",
                  action_type: "",
                  entity_type: "",
                  start_date: "",
                  end_date: "",
                })
              }
              onApplyFilters={() => loadActivityLogs(1)}
              onPagePrev={() =>
                loadActivityLogs(Math.max(1, activityPage - 1))
              }
              onPageNext={() => loadActivityLogs(activityPage + 1)}
              formatDate={formatDate}
            />
          )}

          {activeTab === "stories-analytics" && (
            <StoriesAnalyticsTab
              activeHoursData={activeHoursData}
              popularSearchesData={popularSearchesData}
              categoryInterestData={categoryInterestData}
              analyticsDays={analyticsDays}
              analyticsLoading={analyticsLoading}
              onDaysChange={setAnalyticsDays}
              onRefresh={loadStoriesAnalytics}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesManagerDashboard;
